import { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { CircleUser, Cog, Home, LogOut, Settings, Shield, Users } from 'lucide-react';
import ModelsDialog from './packshots/ModelsDialog';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import ThemeToggle from './ThemeToggle';
import { fetchSession, hasPermission } from '../lib/session';
import { cn } from '@/lib/utils';
import logoDark from '@/assets/images/logo-dark.svg';
import logoLight from '@/assets/images/logo-light.svg';

function navPill(active: boolean): string {
  return cn(
    'inline-flex h-10 items-center gap-2 rounded-full px-4 text-sm font-medium transition-colors',
    active
      ? 'bg-primary/10 text-primary dark:text-white'
      : 'text-muted-foreground hover:bg-muted hover:text-foreground',
  );
}

export default function AppHeader() {
  const [email, setEmail] = useState<string | null>(null);
  const [canReadUsers, setCanReadUsers] = useState(false);
  const [canReadRoles, setCanReadRoles] = useState(false);
  const [canWriteShots, setCanWriteShots] = useState(false);
  const [modelsOpen, setModelsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const homeActive = location.pathname === '/home';
  const usersActive = location.pathname.startsWith('/admin/users');
  const rolesActive = location.pathname.startsWith('/admin/roles');
  const settingsActive = usersActive || rolesActive;
  const showSettings = canReadUsers || canReadRoles || canWriteShots;

  useEffect(() => {
    (async () => {
      const session = await fetchSession();
      if (!session) return;
      setEmail(session.email);
      setCanReadUsers(hasPermission(session, 'users.read'));
      setCanReadRoles(hasPermission(session, 'roles.read'));
      setCanWriteShots(hasPermission(session, 'packshots.write'));
    })();
  }, [location.pathname]);

  async function onLogout() {
    try {
      await fetch('/auth/logout', { method: 'POST' });
    } finally {
      navigate('/');
    }
  }

  return (
    <header className="px-4 pt-4 sm:px-6 lg:px-8">
      <div className="mx-auto flex h-[65px] w-full max-w-[1600px] items-center justify-between gap-4 rounded-full border border-border/80 bg-card/85 px-5 shadow-lg backdrop-blur-xl sm:px-6">
        <div className="flex min-w-0 items-center gap-2">
          <Link to="/packshots" aria-label="Bright.Blue Studio" className="flex shrink-0 items-center gap-2.5">
            <img src={logoLight} alt="" className="h-8 w-[72px] object-contain object-left dark:hidden" />
            <img src={logoDark} alt="" className="hidden h-8 w-[72px] object-contain object-left dark:block" />
            <span className="font-semibold">Studio</span>
          </Link>
          <nav className="flex items-center gap-1.5">
            <Link to="/home" className={navPill(homeActive)} aria-current={homeActive ? 'page' : undefined}>
              <Home className="size-4" aria-hidden="true" />
              Home
            </Link>
            {showSettings && (
              <DropdownMenu>
                <DropdownMenuTrigger className={navPill(settingsActive)} aria-current={settingsActive ? 'page' : undefined}>
                  <Settings className="size-4" aria-hidden="true" />
                  Settings
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  {canWriteShots && (
                    <DropdownMenuItem onSelect={() => setModelsOpen(true)}>
                      <Cog />
                      Models
                    </DropdownMenuItem>
                  )}
                  {canReadUsers && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin/users" aria-current={usersActive ? 'page' : undefined}>
                        <Users />
                        Users
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {canReadRoles && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin/roles" aria-current={rolesActive ? 'page' : undefined}>
                        <Shield />
                        Roles
                      </Link>
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </nav>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <ThemeToggle />
          {email ? (
            <>
              <CircleUser className="size-5 text-muted-foreground" aria-hidden="true" />
              <span className="hidden text-sm text-muted-foreground sm:inline">{email}</span>
              <Button
                size="icon"
                variant="ghost"
                className="size-9"
                onClick={onLogout}
                aria-label="Logout"
              >
                <LogOut />
              </Button>
            </>
          ) : (
            <Button asChild size="sm"><Link to="/">Login</Link></Button>
          )}
        </div>
      </div>
      {canWriteShots && <ModelsDialog open={modelsOpen} onOpenChange={setModelsOpen} />}
    </header>
  );
}
